import React from 'react';
import { Calendar, Clock, User, BookOpen, CheckCircle, AlertTriangle, Lightbulb, ArrowRight, PenTool, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const ContractDraftingAIPage = () => {
  const { language } = useLanguage();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": language === 'fr' 
      ? "Guide Pratique : Rédiger vos Contrats avec l'IA"
      : "Practical Guide: Drafting Contracts with AI",
    "datePublished": "2026-01-16T10:00:00+01:00",
    "dateModified": "2026-01-16T10:00:00+01:00",
    "author": {
      "@type": "Organization",
      "name": "Gaius Legal AI",
      "url": "https://gaius-tech.com"
    }
  };

  const seoTitle = language === 'fr' 
    ? "Rédaction contractuelle avec l'IA : guide pratique"
    : "Contract Drafting with AI: Practical Guide";
    
  const seoDescription = language === 'fr'
    ? "Techniques professionnelles de rédaction de contrats assistée par l'intelligence artificielle. Génération de clauses, personnalisation, optimisation du drafting juridique."
    : "Professional contract drafting techniques assisted by artificial intelligence. Clause generation, customization, legal drafting optimization.";

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title={seoTitle}
          description={seoDescription}
          keywords="rédaction contrat IA, drafting juridique automatisé, génération clauses IA, modèles contrats, legal drafting AI"
          canonicalUrl="https://gaius-tech.com/articles/redaction-contrat-ia"
          alternateUrlFr="https://gaius-tech.com/articles/redaction-contrat-ia"
          alternateUrlEn="https://gaius-tech.com/articles/contract-drafting-ai"
          structuredData={structuredData}
          pageType="article"
        />
        
        {/* Article Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <Badge variant="secondary" className="mb-4 bg-emerald-100 text-emerald-800">Do It Yourself</Badge>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Guide pratique : rédiger vos contrats avec l'IA
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Méthodologies avancées pour la génération de clauses, la personnalisation de modèles et l'optimisation de la rédaction juridique.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>16 janvier 2026</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>20 min de lecture</span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Équipe Gaius</span>
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=400&fit=crop"
              alt="Rédaction contractuelle avec intelligence artificielle"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-lg leading-relaxed">
              La rédaction contractuelle est au cœur de la pratique transactionnelle. Elle exige précision, cohérence et maîtrise des subtilités juridiques propres à chaque type de convention. L'intelligence artificielle générative offre désormais aux praticiens des outils puissants pour accélérer ce processus tout en maintenant les plus hauts standards de qualité.
            </p>

            <p className="text-lg leading-relaxed">
              Ce guide expose les techniques éprouvées par les rédacteurs les plus expérimentés des grands cabinets d'affaires pour exploiter ces technologies de manière professionnelle et responsable.
            </p>

            <h2 className="text-2xl font-bold mt-12 mb-6">I. Principes fondamentaux de la rédaction assistée par IA</h2>

            <h3 className="text-xl font-semibold mt-8 mb-4">1.1 L'IA comme assistant, non comme rédacteur</h3>

            <p className="text-lg leading-relaxed">
              Le premier principe est essentiel : l'intelligence artificielle constitue un outil d'aide à la rédaction, et non un substitut au juriste. Le praticien demeure responsable de :
            </p>

            <ul className="space-y-3 my-6">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>La conception juridique</strong> : définition de la structure contractuelle et des mécanismes juridiques appropriés</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>La validation du contenu</strong> : vérification de l'exactitude juridique et de l'adéquation aux objectifs</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>L'adaptation au contexte</strong> : personnalisation en fonction de l'opération et des parties</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span><strong>La cohérence d'ensemble</strong> : articulation des clauses et prévention des contradictions</span>
              </li>
            </ul>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-8">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-semibold text-lg mb-2">Responsabilité professionnelle</h4>
                  <p className="text-amber-700">
                    L'avocat reste pleinement responsable du contrat produit, quelle que soit l'utilisation d'outils d'IA dans sa rédaction. Une relecture attentive et une validation juridique complète sont impératives avant toute remise au client.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-semibold mt-8 mb-4">1.2 Cas d'usage optimaux de l'IA en rédaction</h3>

            <p className="text-lg leading-relaxed">
              L'IA apporte une valeur ajoutée significative dans plusieurs situations :
            </p>

            <ul className="list-disc pl-6 space-y-2 my-6">
              <li><strong>Premier jet</strong> : génération rapide d'une structure et d'un contenu initial</li>
              <li><strong>Clauses alternatives</strong> : proposition de différentes formulations pour une même disposition</li>
              <li><strong>Adaptation de modèles</strong> : personnalisation de templates existants à un cas particulier</li>
              <li><strong>Traduction juridique</strong> : transposition de clauses entre juridictions ou langues</li>
              <li><strong>Vérification de cohérence</strong> : détection des contradictions et lacunes</li>
            </ul>

            <h2 className="text-2xl font-bold mt-12 mb-6">II. Techniques de génération de clauses</h2>

            <h3 className="text-xl font-semibold mt-8 mb-4">2.1 Structurer ses demandes efficacement</h3>

            <p className="text-lg leading-relaxed">
              La qualité des clauses générées dépend directement de la précision des instructions. Une demande efficace comprend :
            </p>

            <div className="bg-secondary/30 p-6 rounded-lg my-8 font-mono text-sm">
              <p className="mb-4"><strong>Structure type d'un prompt de rédaction :</strong></p>
              <p className="italic">
                « Rédige une clause de [type de clause] pour un [type de contrat] conclu entre [description des parties]. Le contexte est le suivant : [éléments pertinents]. La clause doit [objectifs spécifiques]. Elle doit être conforme au droit [juridiction] et s'inspirer des standards de marché applicables aux transactions de [secteur/type]. Propose trois versions : une version favorable au [partie A], une version équilibrée, et une version favorable au [partie B]. »
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-8 mb-4">2.2 Exemples pratiques</h3>

            <p className="text-lg leading-relaxed">
              <strong>Clause de limitation de responsabilité :</strong>
            </p>

            <div className="bg-secondary/30 p-6 rounded-lg my-8 font-mono text-sm">
              <p className="italic">
                « Rédige une clause de limitation de responsabilité pour un contrat de prestations informatiques entre un éditeur de logiciel SaaS et une grande entreprise du CAC 40. Le prestataire souhaite plafonner sa responsabilité au montant des redevances perçues sur les 12 derniers mois, tout en préservant les exceptions légales impératives (faute lourde, dommages corporels). La clause doit être équilibrée et conforme au droit français. Inclus les préjudices exclus habituels (indirects, perte de données, manque à gagner). »
              </p>
            </div>

            <p className="text-lg leading-relaxed">
              <strong>Clause de non-concurrence post-contractuelle :</strong>
            </p>

            <div className="bg-secondary/30 p-6 rounded-lg my-8 font-mono text-sm">
              <p className="italic">
                « Rédige une clause de non-concurrence pour un pacte d'associés de SAS. Le cédant d'actions (directeur général sortant) s'engage à ne pas concurrencer la société pendant 3 ans après la cession. La clause doit définir précisément : le périmètre géographique (Europe), l'activité concernée (édition de logiciels B2B dans le secteur de la santé), les modalités de la contrepartie financière, et les sanctions en cas de violation. Conforme au droit français et proportionnée pour être valide. »
              </p>
            </div>

            <h2 className="text-2xl font-bold mt-12 mb-6">III. Workflow professionnel de rédaction assistée</h2>

            <h3 className="text-xl font-semibold mt-8 mb-4">3.1 Processus en six étapes</h3>

            <ol className="list-decimal pl-6 space-y-4 my-6">
              <li>
                <strong>Conception</strong> : définir la structure du contrat, les mécanismes juridiques, les points de négociation
              </li>
              <li>
                <strong>Génération</strong> : utiliser l'IA pour produire un premier jet ou des clauses spécifiques
              </li>
              <li>
                <strong>Révision juridique</strong> : vérifier l'exactitude technique, la conformité au droit applicable
              </li>
              <li>
                <strong>Personnalisation</strong> : adapter au contexte spécifique de l'opération et des parties
              </li>
              <li>
                <strong>Cohérence</strong> : s'assurer de l'articulation entre les différentes clauses
              </li>
              <li>
                <strong>Validation finale</strong> : relecture complète par un praticien expérimenté
              </li>
            </ol>

            <h3 className="text-xl font-semibold mt-8 mb-4">3.2 Créer une bibliothèque de prompts</h3>

            <p className="text-lg leading-relaxed">
              Constituez progressivement une bibliothèque de prompts éprouvés pour les clauses récurrentes :
            </p>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-white border rounded-lg p-6">
                <h4 className="font-bold mb-3 flex items-center">
                  <PenTool className="w-5 h-5 text-primary mr-2" />
                  Clauses transactionnelles
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>• Déclarations et garanties</li>
                  <li>• Conditions suspensives</li>
                  <li>• Clauses d'ajustement de prix</li>
                  <li>• Garantie de passif</li>
                  <li>• Non-concurrence</li>
                </ul>
              </div>
              <div className="bg-white border rounded-lg p-6">
                <h4 className="font-bold mb-3 flex items-center">
                  <Scale className="w-5 h-5 text-primary mr-2" />
                  Clauses commerciales
                </h4>
                <ul className="space-y-2 text-sm">
                  <li>• Limitation de responsabilité</li>
                  <li>• Propriété intellectuelle</li>
                  <li>• Confidentialité</li>
                  <li>• Force majeure</li>
                  <li>• Résiliation</li>
                </ul>
              </div>
            </div>

            <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
              <div className="flex items-start">
                <Lightbulb className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-semibold text-lg mb-2">Conseil stratégique</h4>
                  <p className="text-foreground/80">
                    Intégrez dans vos prompts des références à vos modèles internes ou aux standards de marché (AFIC, BVCA, modèles LMA...). L'IA pourra ainsi générer des clauses cohérentes avec vos pratiques habituelles et les attentes du marché.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mt-12 mb-6">IV. Pièges à éviter et bonnes pratiques</h2>

            <h3 className="text-xl font-semibold mt-8 mb-4">4.1 Erreurs courantes</h3>

            <ul className="space-y-3 my-6">
              <li className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                <span><strong>Utiliser des clauses générées sans vérification</strong> : l'IA peut produire des formulations juridiquement incorrectes ou inadaptées</span>
              </li>
              <li className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                <span><strong>Ignorer les définitions</strong> : une clause parfaitement rédigée peut être inefficace si les définitions ne sont pas cohérentes</span>
              </li>
              <li className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                <span><strong>Négliger les interactions</strong> : les clauses doivent fonctionner ensemble (la force majeure impacte la responsabilité, etc.)</span>
              </li>
              <li className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                <span><strong>Oublier le contexte réglementaire</strong> : certaines clauses sont contraintes par des dispositions impératives</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-8 mb-4">4.2 Checklist de validation</h3>

            <ul className="space-y-3 my-6">
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span>Les termes définis sont-ils utilisés de manière cohérente ?</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span>Les clauses respectent-elles le droit impératif applicable ?</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span>Y a-t-il des contradictions entre les différentes dispositions ?</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span>Le style et le niveau de langage sont-ils homogènes ?</span>
              </li>
              <li className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span>Les renvois internes sont-ils exacts ?</span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold mt-12 mb-6">V. Conclusion : vers un drafting augmenté</h2>

            <p className="text-lg leading-relaxed">
              L'intelligence artificielle ne remplace pas l'art de la rédaction juridique : elle l'amplifie. Le praticien qui maîtrise ces outils dispose d'un avantage compétitif considérable en termes de rapidité d'exécution et de capacité à explorer des alternatives.
            </p>

            <p className="text-lg leading-relaxed">
              La clé réside dans une utilisation méthodique, combinant la puissance générative de l'IA avec la rigueur et l'expertise du juriste. Le résultat : des contrats produits plus rapidement, avec une qualité maintenue voire améliorée grâce à la possibilité d'explorer davantage d'options rédactionnelles.
            </p>

          </div>

          {/* CTA Section */}
          <div className="mt-16 pt-8 border-t">
            <h3 className="text-2xl font-bold mb-6">Formations recommandées</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <BookOpen className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Formation Drafting IA</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Maîtrisez les techniques de rédaction contractuelle assistée par IA pour vos opérations.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <PenTool className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Atelier Création de Bibliothèque</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Construisez votre bibliothèque de prompts et modèles personnalisés.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="contract drafting AI, automated legal drafting, AI clause generation, contract templates, legal drafting AI"
        canonicalUrl="https://gaius-tech.com/articles/contract-drafting-ai"
        alternateUrlFr="https://gaius-tech.com/articles/redaction-contrat-ia"
        alternateUrlEn="https://gaius-tech.com/articles/contract-drafting-ai"
        structuredData={structuredData}
        pageType="article"
      />
      
      {/* Article Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4 bg-emerald-100 text-emerald-800">Do It Yourself</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Practical Guide: Drafting Contracts with AI
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Advanced methodologies for clause generation, template customization, and legal drafting optimization.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>January 16, 2026</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>20 min read</span>
              </span>
              <span className="flex items-center space-x-1">
                <User className="w-4 h-4" />
                <span>Gaius Team</span>
              </span>
            </div>
          </div>
          <img
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=400&fit=crop"
            alt="Contract drafting with artificial intelligence"
            className="w-full h-64 object-cover rounded-lg"
          />
        </div>
      </div>

      {/* Article Content - English */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <p className="text-lg leading-relaxed">
            Contract drafting is at the heart of transactional practice. It requires precision, consistency and mastery of the legal subtleties specific to each type of agreement. Generative artificial intelligence now offers practitioners powerful tools to accelerate this process while maintaining the highest quality standards.
          </p>

          <h2 className="text-2xl font-bold mt-12 mb-6">I. Fundamental Principles of AI-Assisted Drafting</h2>

          <p className="text-lg leading-relaxed">
            The first principle is essential: artificial intelligence is a drafting aid tool, not a substitute for the lawyer. The practitioner remains responsible for legal conception, content validation, context adaptation, and overall coherence.
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-8">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-amber-800 font-semibold text-lg mb-2">Professional Responsibility</h4>
                <p className="text-amber-700">
                  The lawyer remains fully responsible for the contract produced, regardless of the use of AI tools in its drafting. Careful review and complete legal validation are imperative before any delivery to the client.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-primary/10 border-l-4 border-primary p-6 my-8">
            <div className="flex items-start">
              <Lightbulb className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-primary font-semibold text-lg mb-2">Strategic Advice</h4>
                <p className="text-foreground/80">
                  Include references to your internal templates or market standards (BVCA, LMA models...) in your prompts. AI can then generate clauses consistent with your usual practices and market expectations.
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* CTA Section */}
        <div className="mt-16 pt-8 border-t">
          <h3 className="text-2xl font-bold mb-6">Recommended Training</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <BookOpen className="w-6 h-6 text-primary" />
                  <h4 className="font-bold">AI Drafting Training</h4>
                </div>
                <p className="text-muted-foreground mb-4">
                  Master AI-assisted contract drafting techniques for your transactions.
                </p>
                <Button asChild>
                  <Link to="/training">
                    Learn more <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <PenTool className="w-6 h-6 text-primary" />
                  <h4 className="font-bold">Library Creation Workshop</h4>
                </div>
                <p className="text-muted-foreground mb-4">
                  Build your library of customized prompts and templates.
                </p>
                <Button asChild>
                  <Link to="/training">
                    Learn more <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
